import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconFilterOff,
  IconFilter,
} from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import SessionBlock from "./components/SessionBlock";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreatePage from "./components/create-page";
import DateInputWithIcon from "./components/DateInputWithIcon";
import { AgentProvider, useAgent } from "contexts/v1/masterData/AgentContext";
import {
  CustomerProvider,
  useCustomer,
} from "contexts/v1/masterData/CustomerContext";
import { LabelProvider, useLabel } from "contexts/v1/masterData/LabelContext";
import Select from "react-select";
import CreatePageBulk from "./components/CreatePageBulk";
import { TripProvider, useTrip } from "contexts/v1/masterData/TripContext";
import { BookingProviderV3, useAppV3 } from "contexts/v3/BookingContextV3";
import { ProductProvider } from "contexts/v1/masterData/ProductContext";
import AsyncSelect from "react-select/async";
import AsyncSelectComponent from "./components/AsyncSelectComponent";
import QuickCreatePage from "./components/quick-create";
import { useAuth } from "hooks/useAuth";
import { PaymentMethodProvider } from "contexts/v1/masterData/PaymentMethodContext";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import { useLastSeen } from "./components/useLastSeen.js";
import { GuideProvider } from "./context/GuideContext";
import { PhotographerProvider } from "./context/PhotographerContext";
import CardClickPage from "./components/card-click";
import { UserProvider } from "contexts/v1/masterData/UserContext";
import io from "socket.io-client";
import RealtimeTerminal from "components/RealtimeTerminal";
import ExportButton from "./components/ExportButton";
import PrintJson from "utils/printJson";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

// 🔹 Komponen utama
const BookingPageV4 = () => {
  const {
    sessions,
    setIsOpenCanvas,
    bookings,
    dataPerTrip,
    filterOptions,
    updateFilter,
    refetch,
    trips,
  } = useAppV3();
  const { customers } = useCustomer();
  const { refetchTrip } = useTrip();
  const { labels } = useLabel();
  const [showFilters, setShowFilters] = useState(true); // State untuk show/hide filter
  const agentData = useAgent(); // ✅ Panggil useAgent() di luar kondisi
  const labelData = useLabel(); // ✅ Panggil useLabel() di luar kondisi

  const dailySummaryQuery = useQuery({
    queryKey: ["booking-v4-daily-summary", filterOptions.filters.reservation_date],
    queryFn: async () => {
      const query = buildQueryString({
        date: filterOptions.filters.reservation_date,
        timezone: "Asia/Makassar",
      });
      return getRequest(`/report/booking-daily-summary${query}`);
    },
    enabled: Boolean(filterOptions.filters.reservation_date),
    staleTime: 60 * 1000,
  });

  const dailySummary = dailySummaryQuery.data?.data;
  // Resume kiri tetap memakai trip-availability karena nilainya berbasis unit ATV.
  // Daily summary memakai pax (tandem x2), sehingga belum boleh menggantikan resume kiri.
  const displaySessions = sessions;

  const { user } = useAuth();
  const userRoles = user?.roles?.map((role) => role.name) || []; // Ambil semua role dalam array
  // console.log("BookingPageV3 userRoles", userRoles);
  const { lastSeen, addLastSeen, clearLastSeen } = useLastSeen(
    "lastSeenBookings",
    5
  );

  const canViewBooking = userRoles.some((role) =>
    ["superadmin", "admin", "reservation"].includes(role)
  );
  const canViewQuickBooking = userRoles.some((role) =>
    ["superadmin", "admin", "reservation"].includes(role)
  );
  const canViewDeveloperTools = userRoles.includes("dev");

  const statusOptions = useMemo(
    () => [
      { value: "booked", label: "Book" },
      { value: "reconfirmed", label: "Re Confirm" },
      { value: "confirmed", label: "Confirmed" },
      { value: "played", label: "Playing" },
      { value: "finished", label: "Finish" },
      { value: "cancelled", label: "Cancelled" },
    ],
    []
  );

  const { handleSuccess, handleError, showCanvasHandler } = useCanvasHandler();

  // 🔹 Optimasi `handleCreate` agar tidak dibuat ulang setiap render
  const handleCreate = useCallback(
    (e) => {
      e.preventDefault();
      showCanvasHandler(
        "Create Reservation",
        <ProductProvider>
          <AgentProvider>
            <LabelProvider>
              <TripProvider>
                <PaymentMethodProvider>
                  <UserProvider>
                    <CreatePage
                      onSuccess={() => {
                        handleSuccess(true, "Reservation created successfully");
                        refetchTrip();
                        refetch();
                      }}
                      onError={handleError}
                      sessions={sessions}
                      customers={customers}
                      labels={labels}
                    />
                  </UserProvider>
                </PaymentMethodProvider>
              </TripProvider>
            </LabelProvider>
          </AgentProvider>
        </ProductProvider>,
        "fullscreen-canvas"
      );
    },
    [
      showCanvasHandler,
      handleSuccess,
      handleError,
      refetch,
      sessions,
      customers,
      refetchTrip,
    ]
  );

  // 🔹 Optimasi `handleQuick` agar tidak dibuat ulang setiap render
  const handleQuick = useCallback(
    (e) => {
      e.preventDefault();
      showCanvasHandler(
        "Quick Reservation",
        <AgentProvider>
          <TripProvider>
            {/* <CreatePageBulk
              onSuccess={() => {
                handleSuccess(true, "Reservation created successfully");
                refetch();
              }}
              onError={handleError}
              sessions={sessions}
              customers={customers}
            /> */}
            <QuickCreatePage
              // onSuccess={handleSuccess}
              onSuccess={() => {
                handleSuccess(true, "Reservation created successfully");
                refetch();
              }}
              onError={handleError}
              sessions={sessions}
              customers={customers}
            />
          </TripProvider>
        </AgentProvider>
      );
    },
    [
      showCanvasHandler,
      handleSuccess,
      handleError,
      refetch,
      sessions,
      customers,
    ]
  );

  const handleLastSeenClick = (booking) => {
    showCanvasHandler(
      `Reservation/Booking #${booking.id}`,
      <LabelProvider>
        <TripProvider>
          <BookingProviderV3>
            <CustomerProvider>
              <AgentProvider>
                <GuideProvider>
                  <PhotographerProvider>
                    <UserProvider>
                      <CardClickPage
                        onSuccess={() => {
                          handleSuccess(true, "Booking updated successfully");
                          refetchTrip();
                          refetch();
                        }}
                        onError={handleError}
                        initialData={booking}
                        refetchTrip={refetchTrip}
                        userRoles={userRoles}
                        updateFilter={updateFilter}
                      />
                    </UserProvider>
                  </PhotographerProvider>
                </GuideProvider>
              </AgentProvider>
            </CustomerProvider>
          </BookingProviderV3>
        </TripProvider>
      </LabelProvider>,
      "fullscreen-canvas"
    );
  };

  const handleRealtimeTerminal = useCallback(() => {
    showCanvasHandler(
      "Realtime Data Terminal",
      <RealtimeTerminal />,
      "fullscreen-canvas"
    );
  }, [showCanvasHandler]);

  // WebSocket connection for real-time booking updates
  useEffect(() => {
    const socketConnection = io("https://realtime-data.gotrasoft.com", {
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      transports: ['polling'],
      timeout: 10000,
    });

    socketConnection.on('connect', () => {
      console.log('[BOOKING_WEBSOCKET] Connected to websocket server using:', socketConnection.io.engine.transport.name);
      socketConnection.emit('join_room', {
        room_id: 'kuber_atv_room'
      });
    });

    socketConnection.on('disconnect', (reason) => {
      console.log('[BOOKING_WEBSOCKET] Disconnected:', reason);
    });

    socketConnection.on('connect_error', (err) => {
      console.warn('[BOOKING_WEBSOCKET] Connection error:', err.message);
    });

    socketConnection.on('new_data', (data) => {
      console.log('[BOOKING_WEBSOCKET] Received new_data event:', data);

      const bookingEventTypes = [
        'booking_created',
        'booking_updated',
        'booking_status_changed',
      ];

      if (data.room_id === 'kuber_atv_room' && bookingEventTypes.includes(data.type)) {
        console.log('[BOOKING_WEBSOCKET] Booking data changed, refreshing booking data');
        refetchTrip();
        refetch();
      }
    });

    socketConnection.on('room_joined', (response) => {
      if (response.success) {
        console.log('[BOOKING_WEBSOCKET] Successfully joined room:', response.room_id);
      } else {
        console.error('[BOOKING_WEBSOCKET] Failed to join room:', response.message);
      }
    });

    return () => {
      if (socketConnection) {
        console.log('[BOOKING_WEBSOCKET] Disconnecting websocket');
        socketConnection.disconnect();
      }
    };
  }, [refetchTrip, refetch]);

  return (
    <LayoutDashboard>
      <div className="row g-3 p-3 justify-content-between">
        <div className="d-flex justify-content-between align-items-center">
          {/* <h5>Filters</h5> */}
          <h1>Reservation V4</h1>

          <div className="d-flex gap-2">
            {canViewDeveloperTools && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  showCanvasHandler(
                    "Debug Raw Data",
                    <PrintJson data={{ sessions, bookings, dataPerTrip, trips }} />,
                    "standard-canvas"
                  );
                }}
                title="View Raw Data"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              </button>
            )}
            <ExportButton
              sessions={displaySessions}
              filterOptions={filterOptions}
              bookings={bookings}
              summary={dailySummary?.summary}
            />

            {canViewDeveloperTools && (
              <button
                className="btn btn-outline-info"
                onClick={handleRealtimeTerminal}
                title="Realtime Data Terminal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
              </button>
            )}

            <button
              className="btn btn-outline-primary"
              onClick={() => setShowFilters(!showFilters)}
          >
            {/* {showFilters ? "Hide Filters" : "Show Filters"} */}
            {showFilters ? (
              <IconFilterOff size={24} />
            ) : (
              <IconFilter size={24} />
            )}
          </button>
          </div>
        </div>
        {/* 🔹 Tombol Booking */}
        {/* <div className="col-md-3 col-sm-6 col-xs-6">
          <button className="btn btn-primary w-100" onClick={handleCreate}>
            <IconPlus size={18} className="me-2" />
            <span>BookingV3</span>
          </button>
        </div> */}

        {/* 🔹 Tombol Booking */}
        {canViewBooking && (
          <div className="col-md-3 col-sm-6 col-xs-6">
            <button className="btn btn-primary w-100" onClick={handleCreate}>
              <IconPlus size={18} className="me-2" />
              <span>Booking</span>
            </button>
          </div>
        )}

        {/* 🔹 Tombol Quick Booking */}
        {canViewQuickBooking && (
          <div className="col-md-3 col-sm-6 col-xs-6">
            <button className="btn btn-dark w-100" onClick={handleQuick}>
              <IconPlus size={18} className="me-2" />
              <span>Quick Booking</span>
            </button>
          </div>
        )}
        {showFilters && (
          <>
            {/* 🔹 Select Agent */}
            <div className="col-md-3 col-sm-6">
              {/* <SelectComponent
            options={agentOption}
            valueKey="agent_id"
            placeholder="Agent"
          /> */}
              <AsyncSelectComponent
                // fetchOptions={useAgent().fetchOptions} // 🚀 Ambil data agent dari API
                fetchOptions={agentData.fetchOptions} // ✅ Gunakan data dari useAgent()
                // fetchOptions={agentOption} // 🚀 Ambil data agent dari API
                valueKey="agent_id"
                placeholder="Search Agent"
              />
            </div>

            {/* 🔹 Select Label */}
            <div className="col-md-3 col-sm-6">
              {/* <SelectComponent
            options={labelOption}
            valueKey="label_id"
            placeholder="Label"
          /> */}
              <AsyncSelectComponent
                // fetchOptions={useLabel().fetchOptions} // 🚀 Ambil data label dari API
                fetchOptions={labelData.fetchOptions} // ✅ Gunakan data dari useLabel()
                valueKey="label_id"
                placeholder="Search Label"
              />
            </div>

            {/* 🔹 Select Status */}
            <div className="col-md-3 col-sm-6">
              <SelectComponent
                options={statusOptions}
                valueKey="status"
                placeholder="Status"
              />
            </div>

            {/* 🔹 Search Input */}
            <div className="col-md-3 col-sm-6">
              <SearchInput />
            </div>

            {/* 🔹 Date Picker */}
            <div className="col-md-3 col-sm-6">
              <DateInputWithIcon
                value={filterOptions.filters.reservation_date}
                onChange={(date) => updateFilter("reservation_date", date)}
              />
            </div>

            {/* 🔹 Navigasi Tanggal */}
            <div className="col-md-3 col-sm-6">
              <DateNavigator />
            </div>
          </>
        )}
        {/* Section Last Seen */}
        <div className="mt-3">
          <h5 className="text-muted d-flex justify-content-between align-items-center">
            Last Seen Bookings :
          </h5>

          {lastSeen.length === 0 ? (
            <p className="text-muted">No recent bookings viewed.</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {lastSeen.map((item) => (
                <button
                  key={item.id}
                  className="btn btn-light btn-sm d-flex align-items-center gap-2"
                  onClick={() => handleLastSeenClick(item)}
                >
                  <i>{item.code}</i>-
                  <b>
                    <b>{item.guest_name}</b>
                  </b>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 🔹 Sessions Grid */}
      <div className="p-4 rounded-lg">
        {displaySessions.map((session) => (
          <SessionBlock
            key={session.id}
            session={session}
            bg_trip={session.bg_trip}
            dataPerTrip={dataPerTrip}
            refetch={refetch}
            trips={trips}
            customers={customers}
            filterOptions={filterOptions}
            userRoles={userRoles}
            updateFilter={updateFilter}
            onAddLastSeen={addLastSeen}
          />
        ))}
      </div>
      <PermissionMiddleware allowedPermissions={["daily_report"]}>
        <div className="p-3 pt-0 rounded-lg card">
          <table className="table table-bordered mb-0">
            <thead>
              <tr>
                <th>SUMMARY</th>
                <th>ATV SINGLE</th>
                <th>ATV TANDEM</th>
                <th>TOTAL PAX</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Total Play</td>
                <td>{dailySummary?.summary?.play?.atv_single ?? 0}</td>
                <td>{dailySummary?.summary?.play?.atv_tandem ?? 0}</td>
                <td>{dailySummary?.summary?.play?.total_pax ?? 0}</td>
              </tr>
              <tr>
                <td>Total Finished</td>
                <td>{dailySummary?.summary?.finished?.atv_single ?? 0}</td>
                <td>{dailySummary?.summary?.finished?.atv_tandem ?? 0}</td>
                <td>{dailySummary?.summary?.finished?.total_pax ?? 0}</td>
              </tr>
              <tr>
                <td>Total Reconfirmed</td>
                <td>{dailySummary?.summary?.reconfirmed?.atv_single ?? 0}</td>
                <td>{dailySummary?.summary?.reconfirmed?.atv_tandem ?? 0}</td>
                <td>{dailySummary?.summary?.reconfirmed?.total_pax ?? 0}</td>
              </tr>
            </tbody>
          </table>
          </div>
      </PermissionMiddleware>
    </LayoutDashboard>
  );
};

export default BookingPageV4;

// 🔹 Reusable Select Component
const SelectComponent = ({ options, valueKey, placeholder }) => {
  const { filterOptions, updateFilter } = useAppV3();
  const selectedValue = useMemo(
    () =>
      options.find((opt) => opt.value === filterOptions.filters[valueKey]) ||
      null,
    [options, filterOptions.filters, valueKey]
  );

  return (
    <Select
      options={options}
      value={selectedValue}
      onChange={(selected) =>
        updateFilter(valueKey, selected ? selected.value : "")
      }
      isClearable
      placeholder={placeholder}
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1050 }) }}
    />
  );
};

// 🔹 Date Navigator Component
const DateNavigator = () => {
  const { filterOptions, updateFilter } = useAppV3();
  const changeDate = (days) => {
    const newDate = new Date(filterOptions.filters.reservation_date);
    newDate.setDate(newDate.getDate() + days);
    updateFilter("reservation_date", newDate.toISOString().split("T")[0]);
  };

  return (
    <div className="btn-group d-flex">
      <button
        className="btn"
        onClick={() => changeDate(-1)}
        title="Previous Day"
      >
        <IconChevronLeft size={18} />
      </button>
      <button className="btn" onClick={() => changeDate(1)} title="Next Day">
        <IconChevronRight size={18} />
      </button>
    </div>
  );
};

// Search Input Component
const SearchInput = () => {
  const { handleSearch, filterOptions } = useAppV3();
  const [search, setSearch] = useState(filterOptions.search || "");

  return (
    <div className="input-icon">
      <input
        type="text"
        className="form-control"
        placeholder="Search by guest name, code, driver"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => handleSearch(e)}
      />

      <span className="input-icon-addon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-search"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <circle cx="10" cy="10" r="7" />
          <line x1="21" y1="21" x2="15" y2="15" />
        </svg>
      </span>
    </div>
  );
};

// buat fungsi untuk create page namun gunakan btn icon
export const CreatePageButton = ({ onClick }) => {
  const {
    sessions,
    setIsOpenCanvas,
    bookings,
    dataPerTrip,
    filterOptions,
    updateFilter,
    refetch,
    trips,
    singleTotal,
    tandemTotal,
    singleTotalFinished, // Tambahkan ke context
    tandemTotalFinished, // Tambahkan ke context
    singleTotalReconfirmed, // Tambahkan ke context
    tandemTotalReconfirmed, // Tambahkan ke context
  } = useAppV3();
  const { customers } = useCustomer();
  const { refetchTrip } = useTrip();
  const { labels } = useLabel();
  const [showFilters, setShowFilters] = useState(true); // State untuk show/hide filter
  const agentData = useAgent(); // ✅ Panggil useAgent() di luar kondisi
  const labelData = useLabel(); // ✅ Panggil useLabel() di luar kondisi
  const { handleSuccess, handleError, showCanvasHandler } = useCanvasHandler();

  const handleCreate = useCallback(
    (e) => {
      e.preventDefault();
      showCanvasHandler(
        "Create Reservation",
        <ProductProvider>
          <AgentProvider>
            <LabelProvider>
              <TripProvider>
                <PaymentMethodProvider>
                  <CreatePage
                    onSuccess={() => {
                      handleSuccess(true, "Reservation created successfully");
                      refetchTrip();
                      refetch();
                    }}
                    onError={handleError}
                    sessions={sessions}
                    customers={customers}
                    labels={labels}
                  />
                </PaymentMethodProvider>
              </TripProvider>
            </LabelProvider>
          </AgentProvider>
        </ProductProvider>,
        "fullscreen-canvas"
      );
    },
    [
      showCanvasHandler,
      handleSuccess,
      handleError,
      refetch,
      sessions,
      customers,
      refetchTrip,
    ]
  );

  return (
    <div className="">
      <button className="btn btn-outline-primary me-2" onClick={handleCreate}>
        <IconPlus size={24} />
      </button>
    </div>
  );
};
